import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Stepper from "../components/Stepper";
import PdfViewer from "../components/PdfViewer";
import { generateBookPdf } from "../services/pdfService";
import { getBookById, updateBook } from "../services/booksService";
import { auth } from "../firebase/firebaseConfig";
import { signOut } from "firebase/auth";
import { getUserProfile } from "../services/userService";

// Text casing helper function
const applyTextCase = (text, caseType) => {
    if (!text) return "";
    if (caseType === "uppercase") {
        return text.toUpperCase();
    } else if (caseType === "lowercase") {
        return text.toLowerCase();
    } else if (caseType === "capitalize") {
        return text.replace(/\b\w/g, char => char.toUpperCase());
    }
    return text;
};

const BookPreview = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [book, setBook] = useState(null);
    const [pdfUrl, setPdfUrl] = useState("");
    const [user, setUser] = useState(null);
    const [publishing, setPublishing] = useState(false);
    const [userProfile, setUserProfile] = useState(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (u) => {
            if (u) {
                setUser(u);
                try {
                    const profile = await getUserProfile(u.uid);
                    setUserProfile(profile);
                } catch (err) {
                    console.error("Error fetching user profile in preview:", err);
                }
            } else {
                navigate("/");
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/");
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    const bookId = new URLSearchParams(location.search).get("bookId");

    useEffect(() => {
        const loadBook = async () => {
            try {
                setLoading(true);
                const bookData = await getBookById(bookId);
                if (!bookData) {
                    alert("Book not found!");
                    navigate("/");
                    return;
                }
                setBook(bookData);
            } catch (error) {
                console.error("Error loading book:", error);
            } finally {
                setLoading(false);
            }
        };

        if (bookId) {
            loadBook();
        } else {
            setLoading(false);
        }
    }, [bookId, navigate]);

    useEffect(() => {
        if (book) {
            generatePdf();
        }
    }, [book]);

    const generatePdf = async () => {
        if (!book) return;

        try {
            setGenerating(true);
            const pdfBytes = await generateBookPdf({
                title: book.title,
                author: book.author,
                content: book.manuscriptContent || "No manuscript content.",
                description: book.description,
                frontCover: book.frontCover,
                backCover: book.backCover,
                aboutAuthor: book.aboutAuthor,
                authorPhoto: book.authorPhoto,
                authorSignature: book.authorSignature,
                coverMode: book.coverMode,
                coverBgColor: book.coverBgColor,
                coverFont: book.coverFont,
                titleColor: book.titleColor,
                titleSize: book.titleSize,
                titleTop: book.titleTop,
                titleLeft: book.titleLeft,
                titleWidth: book.titleWidth,
                titleFontWeight: book.titleFontWeight,
                titleFontStyle: book.titleFontStyle,
                authorColor: book.authorColor,
                authorSize: book.authorSize,
                authorTop: book.authorTop,
                authorLeft: book.authorLeft,
                authorWidth: book.authorWidth,
                authorFontWeight: book.authorFontWeight,
                authorFontStyle: book.authorFontStyle,
                descColor: book.descColor,
                descSize: book.descSize,
                descTop: book.descTop,
                descLeft: book.descLeft,
                descWidth: book.descWidth,
                descFontWeight: book.descFontWeight,
                descFontStyle: book.descFontStyle,
                profileColor: book.profileColor,
                profileSize: book.profileSize,
                profileTop: book.profileTop,
                profileLeft: book.profileLeft,
                profileWidth: book.profileWidth,
                profileFontWeight: book.profileFontWeight,
                profileFontStyle: book.profileFontStyle,
                pageSize: book.pageSize,
                margins: book.margins,
                theme: book.theme,
                headerFooterEnabled: book.headerFooterEnabled,
                pageBorder: book.pageBorder,
                coverTitle: book.coverTitle,
                coverAuthor: book.coverAuthor,
                coverDescription: book.coverDescription,
                coverProfileText: book.coverProfileText,
                titleAlign: book.titleAlign,
                authorAlign: book.authorAlign,
                descAlign: book.descAlign,
                profileAlign: book.profileAlign,
                titleCase: book.titleCase,
                authorCase: book.authorCase,
                descCase: book.descCase,
                profileCase: book.profileCase,
            });

            const blob = new Blob([pdfBytes], {
                type: "application/pdf",
            });

            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
        } catch (error) {
            console.error("PDF generation failed:", error);
            alert("Could not generate book PDF. Please try again.");
        } finally {
            setGenerating(false);
        }
    };

    const handlePublish = async () => {
        if (!bookId) return;
        try {
            setPublishing(true);
            await updateBook(bookId, { status: "published" });
            setBook((prev) => ({ ...prev, status: "published" }));
            alert("Congratulations! Your book has been successfully published! 🎉");
        } catch (error) {
            console.error("Error publishing book:", error);
            alert("Failed to publish book. Please try again.");
        } finally {
            setPublishing(false);
        }
    };

    let wordCount = 0;
    let estPageCount = 2;
    if (book?.manuscriptContent) {
        const mc = book.manuscriptContent;
        if (mc.trim().startsWith("{") || mc.trim().startsWith("[")) {
            try {
                const parsed = JSON.parse(mc);
                const pList = Array.isArray(parsed) ? parsed : (parsed.pages || []);
                estPageCount = pList.length + 2;
                pList.forEach(p => {
                    const pureText = (p.content || "").replace(/<[^>]*>/g, " ");
                    wordCount += pureText.split(/\s+/).filter(Boolean).length;
                });
            } catch (e) {
                wordCount = mc.split(/\s+/).filter(Boolean).length;
                estPageCount = Math.max(1, Math.ceil(wordCount / 300)) + 2;
            }
        } else {
            wordCount = mc.split(/\s+/).filter(Boolean).length;
            estPageCount = Math.max(1, Math.ceil(wordCount / 300)) + 2;
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#F8F7F4] to-[#F0EDE6] font-serif">
            <nav className="bg-white px-6 md:px-10 flex items-center justify-between h-[72px] shadow-[0_2px_12px_rgba(0,0,0,0.08)] border-b border-[#F0F0F0]">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-[#DC2626] rounded-lg flex items-center justify-center text-lg">📚</div>
                    <span className="text-[#1E3A8A] font-extrabold text-xl tracking-tight">
                        Pub<span className="text-[#DC2626]">Tech</span>
                    </span>
                </div>

                <div className="hidden md:flex gap-8 items-center">
                    <Link
                        to="/dashboard"
                        className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Dashboard
                    </Link>
                    <Link
                        to="/create-book"
                        className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Create Book
                    </Link>
                    <Link
                        to="/covers"
                        className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Covers
                    </Link>
                    <Link
                        to="/upload"
                        className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Upload
                    </Link>
                    <Link
                        to="/preview"
                        className="text-[#1E3A8A] border-b-2 border-[#DC2626] text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Preview
                    </Link>
                </div>

                <div className="flex items-center gap-4">
                    <span className="text-gray-500 text-xs font-semibold hidden sm:inline">{user?.email}</span>
                    <button
                        onClick={handleLogout}
                        className="px-4 py-2 bg-transparent hover:bg-[#DC2626] text-[#DC2626] hover:text-white border border-[#DC2626] rounded-lg text-xs font-semibold cursor-pointer transition-all duration-150"
                    >
                        Log Out
                    </button>
                </div>
            </nav>

            <div className="py-10 px-4">
                {loading ? (
                    <div className="max-w-6xl mx-auto">
                        <Stepper currentStep={4} />
                        <div className="mt-8 flex flex-col items-center justify-center p-16 bg-white rounded-2xl shadow-xl border border-gray-100">
                            <div className="relative">
                                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-100 border-t-red-600"></div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-2xl">📚</span>
                                </div>
                            </div>
                            <p className="mt-6 text-gray-700 font-medium text-lg animate-pulse">
                                Loading Book Preview...
                            </p>
                            <p className="text-sm text-gray-500 mt-2">Preparing your book for review</p>
                        </div>
                    </div>
                ) : (!bookId || !book) ? (
                    <div className="max-w-3xl mx-auto text-center bg-white rounded-2xl shadow-xl p-12 border border-gray-100">
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="text-4xl">📖</span>
                        </div>
                        <h2 className="text-2xl font-bold text-blue-900 mb-3">No active book selection</h2>
                        <p className="text-gray-600 mb-8">Please select a book first to preview.</p>
                        <button
                            onClick={() => navigate("/dashboard")}
                            className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white font-semibold px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                        >
                            Go to Dashboard
                        </button>
                    </div>
                ) : (
                    <div className="max-w-6xl mx-auto">
                        {/* Header */}
                        <div className="mb-8 text-center">
                            <div className="inline-flex items-center gap-2 bg-blue-900/10 border border-blue-900/20 rounded-full px-4 py-1.5 mb-4">
                                <span className="w-1.5 h-1.5 bg-red-600 rounded-full inline-block" />
                                <span className="text-blue-900 text-xs font-bold tracking-widest uppercase">
                                    Final Step
                                </span>
                            </div>
                            <h1 className="text-4xl font-extrabold text-blue-900 mb-3 tracking-tight">
                                Book <span className="text-red-600">Preview</span>
                            </h1>
                            <p className="text-gray-600 max-w-2xl mx-auto">
                                Review your complete book with cover art, formatted content, and generate a print-ready PDF.
                            </p>
                        </div>

                        <Stepper currentStep={4} />

                        <div className="grid lg:grid-cols-3 gap-8 mt-8">
                            {/* Book Details and Status */}
                            <div className="lg:col-span-2 space-y-6">
                                {/* Book Info Card */}
                                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                                    <div className="h-1.5 bg-gradient-to-r from-red-600 to-blue-900" />
                                    <div className="p-6">
                                        <div className="flex items-center gap-3 mb-4">
                                            {book.status === "published" ? (
                                                <span className="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-700 text-xs font-bold px-3 py-1.5 rounded-full">
                                                    <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full inline-block" />
                                                    Published 🚀
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-bold px-3 py-1.5 rounded-full">
                                                    <span className="w-1.5 h-1.5 bg-green-600 rounded-full inline-block animate-pulse" />
                                                    Draft Ready
                                                </span>
                                            )}
                                            <span className="text-xs text-gray-500">
                                                Last updated: Today
                                            </span>
                                        </div>

                                        <h1 className="text-3xl font-extrabold text-blue-900 mb-1 tracking-tight">
                                            {book.title}
                                        </h1>
                                        <p className="text-lg text-gray-600 font-medium mb-4">
                                            By <span className="text-blue-900">{book.author}</span>
                                        </p>

                                        <div className="border-t border-gray-100 pt-4 grid grid-cols-3 gap-3">
                                            <div className="p-3 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100">
                                                <span className="block text-xs text-gray-500 font-semibold uppercase tracking-wider">Genre</span>
                                                <span className="text-sm font-bold text-blue-900">{book.genre || "N/A"}</span>
                                            </div>
                                            <div className="p-3 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100">
                                                <span className="block text-xs text-gray-500 font-semibold uppercase tracking-wider">Words</span>
                                                <span className="text-sm font-bold text-blue-900">{wordCount}</span>
                                            </div>
                                            <div className="p-3 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100">
                                                <span className="block text-xs text-gray-500 font-semibold uppercase tracking-wider">Est. Pages</span>
                                                <span className="text-sm font-bold text-blue-900">{estPageCount}</span>
                                            </div>
                                        </div>

                                        {book.description && (
                                            <div className="mt-6">
                                                <h3 className="font-bold text-blue-900 text-sm mb-1 flex items-center gap-2">
                                                    <span className="text-red-600">📝</span>
                                                    Book Description
                                                </h3>
                                                <p className="text-gray-600 text-sm italic bg-gray-50 p-4 rounded-xl border border-gray-100">
                                                    "{book.description}"
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* PDF Generation Card */}
                                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                                    <div className="h-1.5 bg-gradient-to-r from-blue-900 to-red-600" />
                                    <div className="p-6 text-center">
                                        <div className="flex items-center justify-center gap-3 mb-3">
                                            <span className="text-3xl">📄</span>
                                            <h3 className="text-xl font-bold text-blue-900">Publishing Action Center</h3>
                                        </div>
                                        <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                                            Generate a print-ready PDF layout of your book, or publish it to the platform.
                                        </p>

                                        {generating ? (
                                            <div className="flex flex-col items-center justify-center py-4">
                                                <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-100 border-t-red-600 mb-3"></div>
                                                <p className="text-sm text-blue-900 font-semibold animate-pulse">Generating book PDF layout...</p>
                                            </div>
                                        ) : pdfUrl ? (
                                            <div className="flex flex-wrap justify-center gap-4">
                                                {book.status !== "published" ? (
                                                    (userProfile?.role === "publisher" || userProfile?.role === "admin") ? (
                                                        <button
                                                            onClick={handlePublish}
                                                            disabled={publishing}
                                                            className="bg-blue-900 text-white font-bold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 flex items-center gap-2">
                                                            🚀 {publishing ? "Publishing..." : "Publish Book"}
                                                        </button>
                                                    ) : (
                                                        <span className="bg-amber-50 text-amber-800 font-bold px-8 py-3.5 rounded-xl border border-amber-200 flex items-center gap-2">
                                                            ⏳ Awaiting Publisher Review
                                                        </span>
                                                    )
                                                ) : (
                                                    <span className="bg-indigo-100 text-indigo-700 font-bold px-8 py-3.5 rounded-xl border border-indigo-300 flex items-center gap-2">
                                                        ✓ Published 🚀
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <button
                                                onClick={generatePdf}
                                                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 flex items-center gap-2 mx-auto"
                                            >
                                                Generate Preview PDF
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Artwork Previews */}
                            <div className="space-y-6">
                                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden sticky top-24">
                                    <div className="h-1.5 bg-gradient-to-r from-red-600 to-blue-900" />
                                    <div className="p-5">
                                        <h3 className="font-bold text-blue-900 mb-4 text-sm uppercase tracking-wide flex items-center gap-2">
                                            <span className="text-red-600">🎨</span>
                                            Selected Cover Art
                                        </h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            {(() => {
                                                const getBookAspect = (type) => {
                                                    const types = {
                                                        pocket_book: { w: 4.25, h: 6.87 },
                                                        small_paperback: { w: 5, h: 8 },
                                                        digest_size: { w: 5.5, h: 8.5 },
                                                        trade_paperback: { w: 6, h: 9 },
                                                        large_book: { w: 7, h: 10 },
                                                        childrens_book: { w: 8, h: 10 },
                                                        square_book: { w: 8.5, h: 8.5 },
                                                        textbook: { w: 8.5, h: 11 },
                                                        a5: { w: 5.83, h: 8.27 },
                                                        a4: { w: 8.27, h: 11.69 },
                                                    };
                                                    const dim = types[type] || types.trade_paperback;
                                                    return `${dim.w} / ${dim.h}`;
                                                };

                                                const getFontFamily = (f) => {
                                                    if (f === "sans-serif") return "system-ui, -apple-system, sans-serif";
                                                    if (f === "monospace") return "Courier New, monospace";
                                                    return "Georgia, Times New Roman, serif";
                                                };

                                                const getPositionStyles = (pos) => {
                                                    if (pos === "top") return { justifyContent: "flex-start", paddingTop: "16px" };
                                                    if (pos === "bottom") return { justifyContent: "flex-end", paddingBottom: "16px" };
                                                    return { justifyContent: "center" };
                                                };

                                                const previewTextStyle = {
                                                    fontFamily: getFontFamily(book.coverFont),
                                                    color: book.coverTextColor || "#FFFFFF",
                                                };

                                                const barcodeHtml = (
                                                    <div className="bg-white p-0.5 rounded flex flex-col items-center justify-between h-7 w-11 shadow-xs border border-gray-200">
                                                        <div className="flex items-end justify-center space-x-[0.5px] h-4 w-full">
                                                            {Array.from({ length: 11 }).map((_, i) => (
                                                                <div
                                                                    key={i}
                                                                    className="bg-black"
                                                                    style={{
                                                                        width: i % 3 === 0 ? '1.5px' : '0.8px',
                                                                        height: i % 4 === 0 ? '100%' : '80%',
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                        <span className="text-[3px] text-gray-800 font-mono tracking-tighter leading-none scale-[0.8]">
                                                            978-3-16-148410-0
                                                        </span>
                                                    </div>
                                                );

                                                return (
                                                    <>
                                                        <div>
                                                            <span className="block text-xs text-gray-500 mb-2 font-semibold text-center uppercase tracking-wide">Front Cover</span>
                                                            <div 
                                                                style={{
                                                                    backgroundColor: book.coverBgColor || "#DC2626",
                                                                    aspectRatio: getBookAspect(book.bookType),
                                                                    ...(book.coverMode === "image" && book.frontCover ? {
                                                                        backgroundImage: `url(${book.frontCover})`,
                                                                        backgroundSize: 'cover',
                                                                        backgroundPosition: 'center',
                                                                    } : {})
                                                                }}
                                                                className="relative w-full rounded-lg overflow-hidden shadow-md border-2 border-gray-200 select-none p-3"
                                                            >
                                                                {/* Overlay gradient */}
                                                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/30 pointer-events-none z-0" />

                                                                {/* Title */}
                                                                <div
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: `${book.titleTop !== undefined ? book.titleTop : 35}%`,
                                                                        left: `${book.titleLeft !== undefined ? book.titleLeft : 10}%`,
                                                                        color: book.titleColor || "#FFFFFF",
                                                                        fontSize: `${(book.titleSize || 28) * 0.4}px`,
                                                                        fontFamily: getFontFamily(book.coverFont),
                                                                        fontWeight: book.titleFontWeight || "bold",
                                                                        fontStyle: book.titleFontStyle || "normal",
                                                                        textAlign: book.titleAlign || "center",
                                                                        width: `${book.titleWidth !== undefined ? book.titleWidth : 60}%`,
                                                                        wordBreak: "break-word",
                                                                        zIndex: 10,
                                                                        textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                                                                        textTransform: book.titleCase || "uppercase",
                                                                    }}
                                                                    dangerouslySetInnerHTML={{ __html: book.coverTitle !== undefined ? book.coverTitle : (book.title || "Untitled") }}
                                                                />

                                                                {/* Author */}
                                                                <div
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: `${book.authorTop !== undefined ? book.authorTop : 75}%`,
                                                                        left: `${book.authorLeft !== undefined ? book.authorLeft : 10}%`,
                                                                        color: book.authorColor || "#FFFFFF",
                                                                        fontSize: `${(book.authorSize || 16) * 0.4}px`,
                                                                        fontFamily: getFontFamily(book.coverFont),
                                                                        fontWeight: book.authorFontWeight || "bold",
                                                                        fontStyle: book.authorFontStyle || "normal",
                                                                        textAlign: book.authorAlign || "center",
                                                                        width: `${book.authorWidth !== undefined ? book.authorWidth : 60}%`,
                                                                        wordBreak: "break-word",
                                                                        zIndex: 10,
                                                                        textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                                                                        textTransform: book.authorCase || "uppercase",
                                                                    }}
                                                                    dangerouslySetInnerHTML={{ __html: book.coverAuthor !== undefined ? book.coverAuthor : (book.author || "Author") }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span className="block text-xs text-gray-500 mb-2 font-semibold text-center uppercase tracking-wide">Back Cover</span>
                                                            <div 
                                                                style={{
                                                                    backgroundColor: book.coverBgColor || "#DC2626",
                                                                    aspectRatio: getBookAspect(book.bookType),
                                                                    ...(book.coverMode === "image" && book.backCover ? {
                                                                        backgroundImage: `url(${book.backCover})`,
                                                                        backgroundSize: 'cover',
                                                                        backgroundPosition: 'center',
                                                                    } : {})
                                                                }}
                                                                className="relative w-full rounded-lg overflow-hidden shadow-md border-2 border-gray-200 select-none p-3"
                                                            >
                                                                {/* Overlay gradient */}
                                                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/30 pointer-events-none z-0" />

                                                                {/* Description Blurb */}
                                                                <div
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: `${book.descTop !== undefined ? book.descTop : 15}%`,
                                                                        left: `${book.descLeft !== undefined ? book.descLeft : 8}%`,
                                                                        color: book.descColor || "#FFFFFF",
                                                                        fontSize: `${(book.descSize || 13) * 0.45}px`,
                                                                        fontFamily: getFontFamily(book.coverFont),
                                                                        fontWeight: book.descFontWeight || "normal",
                                                                        fontStyle: book.descFontStyle || "italic",
                                                                        textAlign: book.descAlign || "left",
                                                                        padding: "6px",
                                                                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                                                                        borderLeft: `2px solid ${book.descColor || "#FFFFFF"}`,
                                                                        borderRadius: "4px",
                                                                        width: `${book.descWidth !== undefined ? book.descWidth : 60}%`,
                                                                        wordBreak: "break-word",
                                                                        zIndex: 10,
                                                                        lineHeight: 1.3,
                                                                        textTransform: book.descCase || "normal",
                                                                    }}
                                                                >
                                                                    <div dangerouslySetInnerHTML={{ __html: book.coverDescription !== undefined ? book.coverDescription : (book.aboutAuthor || `${book.author || "The author"} is the visionary creator of this book.`) }} />
                                                                    {book.authorSignature && (
                                                                        <div style={{ marginTop: "6px", display: "flex", justifyContent: (book.descAlign || "left") === "center" ? "center" : (book.descAlign || "left") === "right" ? "flex-end" : "flex-start" }}>
                                                                            <img
                                                                                src={book.authorSignature}
                                                                                alt="Signature"
                                                                                style={{
                                                                                    maxHeight: "20px",
                                                                                    maxWidth: "70px",
                                                                                    objectFit: "contain",
                                                                                    filter: (book.descColor || "#FFFFFF") !== "#000000" ? "brightness(0) invert(1)" : "none",
                                                                                }}
                                                                                className="pointer-events-none select-none"
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Profile Row */}
                                                                <div
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: `${book.profileTop !== undefined ? book.profileTop : 65}%`,
                                                                        left: `${book.profileLeft !== undefined ? book.profileLeft : 8}%`,
                                                                        color: book.profileColor || "#FFFFFF",
                                                                        fontFamily: getFontFamily(book.coverFont),
                                                                        padding: "6px",
                                                                        backgroundColor: "rgba(0, 0, 0, 0.6)",
                                                                        borderRadius: "6px",
                                                                        width: `${book.profileWidth !== undefined ? book.profileWidth : 60}%`,
                                                                        zIndex: 10,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "6px",
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={book.authorPhoto || `https://image.pollinations.ai/prompt/professional%20headshot%20portrait%20of%20author%20named%20${encodeURIComponent(book.author || "author")}%20wearing%20smart%20casual%20attire%20studio%20lighting%20plain%20background?width=100&height=100&nologo=true&seed=42`}
                                                                        alt="Author"
                                                                        className="w-7 h-7 rounded-full object-cover border border-white/40 pointer-events-none select-none"
                                                                        onError={(e) => {
                                                                            e.target.src = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop&q=60";
                                                                        }}
                                                                    />
                                                                    <div style={{ flex: 1, textAlign: book.profileAlign || "left", pointerEvents: "none" }}>
                                                                        <div style={{ fontSize: `${(book.profileSize || 10) * 0.5 + 1.5}px`, fontWeight: book.authorFontWeight || "bold", fontStyle: book.authorFontStyle || "normal", lineHeight: 1.1, textTransform: book.authorCase || "none" }}
                                                                             dangerouslySetInnerHTML={{ __html: book.coverAuthor !== undefined ? book.coverAuthor : (book.author || "Author") }}
                                                                        />
                                                                        <div style={{ fontSize: `${(book.profileSize || 10) * 0.45}px`, fontWeight: book.profileFontWeight || "normal", fontStyle: book.profileFontStyle || "italic", opacity: 0.9, marginTop: "1px", lineHeight: 1.2, textTransform: book.profileCase || "none" }}
                                                                             dangerouslySetInnerHTML={{ __html: book.coverProfileText !== undefined ? book.coverProfileText : (book.description || "A fascinating journey through character and destiny.") }}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {/* Fixed Barcode */}
                                                                <div className="absolute bottom-3 right-3 bg-white p-0.5 rounded flex flex-col items-center justify-center h-5 w-8 shadow-md border border-gray-200 z-10 pointer-events-none">
                                                                    <div className="flex items-end justify-center space-x-[0.5px] h-3 w-full">
                                                                        {Array.from({ length: 11 }).map((_, i) => (
                                                                            <div
                                                                                key={i}
                                                                                className="bg-black"
                                                                                style={{
                                                                                    width: i % 3 === 0 ? '1px' : '0.5px',
                                                                                    height: i % 4 === 0 ? '100%' : '80%',
                                                                                }}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {pdfUrl && (
                            <div className="mt-10 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                                <div className="h-1.5 bg-gradient-to-r from-red-600 via-blue-900 to-red-600" />
                                <div className="p-6">
                                    <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
                                        <div className="flex items-center gap-3">
                                            <span className="text-2xl">📄</span>
                                            <h2 className="text-xl font-bold text-blue-900">Print Preview PDF</h2>
                                            <span className="text-xs bg-green-100 text-green-700 font-bold px-3 py-1 rounded-full">
                                                Ready
                                            </span>
                                        </div>
                                    </div>
                                    <div className="border-2 border-gray-200 rounded-xl overflow-hidden shadow-inner">
                                        <PdfViewer pdfUrl={pdfUrl} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

/* ── Shared style tokens (Commented out - migrated to Tailwind CSS) ────────
const navStyles = {
    page: {
        minHeight: "100vh",
        background: "linear-gradient(160deg, #F8F7F4 0%, #F0EDE6 100%)",
        fontFamily: "'Georgia', 'Times New Roman', serif",
    },
    nav: {
        background: "#FFFFFF",
        padding: "0 40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "72px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        borderBottom: "1px solid #F0F0F0",
    },
    navLeft: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    logo: {
        width: "36px",
        height: "36px",
        background: "#DC2626",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "18px",
    },
    logoText: {
        color: "#1E3A8A",
        fontWeight: "800",
        fontSize: "20px",
        letterSpacing: "-0.02em",
    },
    logoRed: {
        color: "#DC2626",
    },
    navLinks: {
        display: "flex",
        gap: "32px",
        alignItems: "center",
    },
    navLink: {
        color: "#4B5563",
        fontSize: "14px",
        fontWeight: "500",
        textDecoration: "none",
        transition: "color 0.15s",
        padding: "8px 0",
        borderBottom: "2px solid transparent",
    },
    navLinkActive: {
        color: "#1E3A8A",
        borderBottom: "2px solid #DC2626",
    },
    navRight: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
    },
    userEmail: {
        color: "#6B7280",
        fontSize: "13px",
        fontWeight: "500",
    },
    logoutBtn: {
        padding: "8px 16px",
        background: "transparent",
        color: "#DC2626",
        border: "1px solid #DC2626",
        borderRadius: "8px",
        fontSize: "13px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all 0.15s",
    },
};
*/

export default BookPreview;